<template>
  <div class="mor-panel">
    <MaintenanceOSInstanceDetail
      v-if="selectedOsId"
      :os-id="selectedOsId"
      :access-level="accessLevel"
      @back="selectedOsId = ''"
      @changed="reload"
    />

    <template v-else>
      <div class="mor-filters">
        <select class="mor-select" v-model="filters.projectId" @change="reload">
          <option value="">Todos os projetos</option>
          <option v-for="p in projects" :key="p.id" :value="p.id">{{ p.name }}</option>
        </select>
        <select class="mor-select" v-model="filters.status" @change="reload">
          <option value="">Todos os status</option>
          <option value="pending">Em andamento</option>
          <option value="approved">Aprovadas</option>
          <option value="cancelled">Canceladas</option>
        </select>
        <button v-if="accessLevel >= 3" class="mor-btn mor-btn-primary" type="button" @click="exportExcel">Exportar Excel</button>
      </div>

      <div v-if="loading" class="mor-loading">Carregando...</div>
      <div v-else-if="typeGroups.length === 0" class="mor-empty">Nenhuma OS encontrada.</div>
      <div v-else class="mor-list">
        <div v-for="group in typeGroups" :key="group.rootId" class="mor-type-card">
          <div class="mor-type-title">{{ group.title }}</div>
          <div class="mor-instances">
            <button v-for="inst in group.instances" :key="inst.id" class="mor-instance-row" type="button" @click="selectedOsId = inst.id">
              <span class="mor-instance-number">OS{{ inst.osNumber }}</span>
              <span class="mor-instance-meta">{{ inst.projectName }} · {{ inst.responsaveis.join(', ') }}</span>
              <span class="mor-instance-meta">
                {{ inst.lastCompletedAt ? `preenchida em ${formatDateTime(inst.lastCompletedAt)}${inst.lastCompletedByName ? ' por ' + inst.lastCompletedByName : ''}` : 'não preenchida' }}
              </span>
              <span class="mor-status-badge" :class="'s-' + inst.status">{{ statusLabel(inst.status) }}</span>
            </button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, reactive, ref, type PropType } from 'vue'
import * as XLSX from 'xlsx'
import { maintenanceStore, type ServiceOrder, type ServiceOrderExportRow } from '@/store/maintenance'
import { projectsStore } from '@/store/projects'
import type { Project } from '@/store/projects'
import MaintenanceOSInstanceDetail from './MaintenanceOSInstanceDetail.vue'

interface OsTypeGroup {
  rootId: string
  title: string
  instances: ServiceOrder[]
}

export default defineComponent({
  name: 'MaintenanceOSRealizadasTab',
  components: { MaintenanceOSInstanceDetail },
  props: {
    project: { type: Object as PropType<Project>, required: true },
    accessLevel: { type: Number, default: 0 }
  },
  setup(props) {
    const loading = ref(true)
    const selectedOsId = ref('')
    const filters = reactive({ projectId: props.project.id, status: '' })

    const orders = computed(() => maintenanceStore.serviceOrders.items)
    const projects = computed(() => projectsStore.all())

    const typeGroups = computed<OsTypeGroup[]>(() => {
      const map = new Map<string, OsTypeGroup>()
      for (const os of orders.value) {
        const rootId = os.sourceOsId || os.id
        let group = map.get(rootId)
        if (!group) {
          group = { rootId, title: os.title, instances: [] }
          map.set(rootId, group)
        }
        group.instances.push(os)
      }
      const groups = Array.from(map.values())
      groups.forEach(g => g.instances.sort((a, b) => b.osNumber - a.osNumber))
      return groups.sort((a, b) => a.title.localeCompare(b.title))
    })

    const statusLabel = (s: string) => ({ pending: 'Em andamento', approved: 'Aprovada', cancelled: 'Cancelada' }[s] || s)
    const formatDateTime = (iso: string) => iso ? new Date(iso).toLocaleString('pt-BR') : ''

    const reload = async () => {
      loading.value = true
      try {
        await maintenanceStore.loadServiceOrders({ projectId: filters.projectId, status: filters.status })
      } finally {
        loading.value = false
      }
    }

    const exportExcel = async () => {
      const rows = await maintenanceStore.fetchServiceOrdersExport({ projectId: filters.projectId, status: filters.status })
      const header = ['OS', 'Rotina', 'Projeto', 'Prioridade', 'Status', 'Concluído por', 'Concluído em', 'Revisado por', 'Observação', 'Nº de refações']
      const data = rows.map((r: ServiceOrderExportRow) => [
        r.osTitle, r.itemTitle, r.projectName, r.priority, r.status,
        r.completedByName, r.completedAt ? formatDateTime(r.completedAt) : '', r.reviewedByName, r.reviewNotes, r.reworkCount
      ])
      const workbook = XLSX.utils.book_new()
      const sheet = XLSX.utils.aoa_to_sheet([header, ...data])
      sheet['!cols'] = [{ wch: 26 }, { wch: 26 }, { wch: 22 }, { wch: 10 }, { wch: 16 }, { wch: 22 }, { wch: 18 }, { wch: 22 }, { wch: 30 }, { wch: 10 }]
      XLSX.utils.book_append_sheet(workbook, sheet, 'OS Realizadas')
      const content = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' })
      const blob = new Blob([content], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'os-realizadas.xlsx'
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    }

    onMounted(async () => {
      if (projectsStore.all().length === 0) await projectsStore.refresh()
      await reload()
    })

    return { loading, selectedOsId, filters, projects, typeGroups, statusLabel, formatDateTime, reload, exportExcel }
  }
})
</script>

<style scoped lang="scss">
.mor-panel { display: flex; flex-direction: column; gap: 16px; }
.mor-filters { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; }
.mor-select {
  border: 1px solid rgba(31,30,26,.18); border-radius: 8px; padding: 8px 10px;
  font-size: 13px; color: var(--ink); background: var(--bg, #fff); font-family: inherit;
}
.mor-btn {
  border: none; border-radius: 8px; padding: 9px 16px; font-size: 13px; font-weight: 600; cursor: pointer;
  background: rgba(31,30,26,.08); color: var(--ink);
}
.mor-btn-primary { background: var(--accent); color: #fff; }

.mor-loading, .mor-empty { font-size: 13px; color: var(--ink-muted); padding: 24px 0; text-align: center; }

.mor-list { display: flex; flex-direction: column; gap: 12px; }
.mor-type-card {
  background: var(--card, #fff); border: 1px solid rgba(31,30,26,.1); border-radius: 12px;
  padding: 14px 16px; display: flex; flex-direction: column; gap: 10px;
}
.mor-type-title { font-size: 15px; font-weight: 700; color: var(--ink); }

.mor-instances { display: flex; flex-direction: column; gap: 6px; }
.mor-instance-row {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  background: var(--bg, #f7f4ee); border: none; border-radius: 8px;
  padding: 8px 12px; cursor: pointer; text-align: left;
  &:hover { background: rgba(31,30,26,.06); }
}
.mor-instance-number { font-size: 13px; font-weight: 700; color: var(--ink); }
.mor-instance-meta { font-size: 12px; color: var(--ink-muted); }

.mor-status-badge {
  display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 11px; font-weight: 700; margin-left: auto;
  &.s-pending { background: rgba(217,119,6,.12); color: #d97706; }
  &.s-approved { background: rgba(22,163,74,.12); color: #16a34a; }
  &.s-cancelled { background: rgba(100,116,139,.12); color: #475569; }
}
</style>
